import { fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../app/store'
import { setToken, logout } from '../app/authSlice'

// Fila para controlar refreshes simultâneos
let refreshPromise: Promise<string | null> | null = null

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_SERVER_URL_BASE,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.userToken
    // If we have a token set in state, let's assume that we should be passing it.
    if (token) {
      headers.set('authorization', `JWT ${token}`)
    }
    return headers
  },
})

const baseQueryNoAuth = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_SERVER_URL_BASE,
})

// Função para executar o refresh do token
const executeRefresh = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  api: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraOptions: any,
): Promise<string | null> => {
  const state = api.getState() as RootState
  const refreshToken =
    // prefer state, fallback to localStorage to survive reloads
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state as any)?.auth?.refreshToken ||
    (typeof window !== 'undefined'
      ? localStorage.getItem('refreshToken')
      : null)

  if (!refreshToken) {
    api.dispatch(logout())
    if (typeof window !== 'undefined') {
      localStorage.clear()
      if (window.location.pathname !== '/login') {
        window.location.href = '/logout'
      }
    }
    return null
  }

  const refreshResponse = await baseQueryNoAuth(
    {
      url: '/api/v1/authentication/token/web/refresh/',
      method: 'POST',
      body: { refresh: refreshToken },
      headers: { 'Content-Type': 'application/json' },
    },
    api,
    extraOptions,
  )

  // @ts-expect-error - fetchBaseQuery returns a union; we only need data.access here
  const newAccess: string | undefined = refreshResponse?.data?.access
  if (newAccess) {
    api.dispatch(setToken(newAccess))
    return newAccess
  }

  return null
}

const customBaseQuery: typeof baseQuery = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions)

  if (result.error && result.error.status === 401) {
    const requestedUrl = typeof args === 'string' ? args : args.url
    const isAuthOrRefresh =
      requestedUrl?.includes('/api/v1/authentication/token/web/auth/') ||
      requestedUrl?.includes('/api/v1/authentication/token/web/refresh/')

    if (!isAuthOrRefresh) {
      if (refreshPromise) {
        const newAccess = await refreshPromise
        if (newAccess) {
          return await baseQuery(args, api, extraOptions)
        }
      } else {
        refreshPromise = executeRefresh(api, extraOptions)
        const newAccess = await refreshPromise
        refreshPromise = null

        if (newAccess) {
          return await baseQuery(args, api, extraOptions)
        }
      }

      api.dispatch(logout())
      if (
        typeof window !== 'undefined' &&
        window.location.pathname !== '/login'
      ) {
        window.location.href = '/logout'
      }
      return result
    }
  }

  if (result.error && result.error.status === 403) {
    console.warn('Acesso não autorizado...')
    if (window.location.pathname !== '/login') {
      window.location.href = '/unauthorized'
    }
  }

  return result
}

export const cloudBaseUrl =
  import.meta.env.VITE_SERVER_URL_BASE === 'http://localhost:8000'
    ? 'http://localhost:3001'
    : import.meta.env.VITE_SERVER_URL_BASE

const cloudBaseQuery = fetchBaseQuery({
  baseUrl: cloudBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.userToken
    if (token) {
      headers.set('authorization', `JWT ${token}`)
    }
    return headers
  },
})

const customCloudBaseQuery: typeof cloudBaseQuery = async (
  args,
  api,
  extraOptions,
) => {
  const result = await cloudBaseQuery(args, api, extraOptions)

  if (result.error && result.error.status === 401) {
    const requestedUrl = typeof args === 'string' ? args : args.url
    const isAuthOrRefresh =
      requestedUrl?.includes('/api/v1/authentication/token/web/auth/') ||
      requestedUrl?.includes('/api/v1/authentication/token/web/refresh/')

    if (!isAuthOrRefresh) {
      if (refreshPromise) {
        const newAccess = await refreshPromise
        if (newAccess) {
          return await cloudBaseQuery(args, api, extraOptions)
        }
      } else {
        refreshPromise = executeRefresh(api, extraOptions)
        const newAccess = await refreshPromise
        refreshPromise = null

        if (newAccess) {
          return await cloudBaseQuery(args, api, extraOptions)
        }
      }

      api.dispatch(logout())
      if (
        typeof window !== 'undefined' &&
        window.location.pathname !== '/login'
      ) {
        window.location.href = '/logout'
      }
      return result
    }
  }

  if (result.error && result.error.status === 403) {
    console.warn('Acesso não autorizado...')
    if (window.location.pathname !== '/login') {
      window.location.href = '/unauthorized'
    }
  }

  return result
}

export default customBaseQuery
export { customCloudBaseQuery }
